import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { format, parseISO } from "date-fns";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CsrStatusFilterSelect } from "../../../components/CsrStatusFilterSelect";
import { CsrStatusSelect } from "../../../components/CsrStatusSelect";
import { CsrStatus, TripWithTenant } from "../../../types";
import { isInfinityDate } from "../../../utils/dateHelpers";
import {
  getTripType,
  tripTypeColors,
  tripTypeShortLabels,
  TripType,
} from "../../../utils/tripTypeHelpers";
import { ConfirmationDialog } from "../../../widgets/ConfirmationDialog/ConfirmationDialog";
import { ScheduleBulkActionBar } from "./components/ScheduleBulkActionBar";

interface ScheduleListPresenterProps {
  // Data
  filteredTrips: TripWithTenant[];
  trips: TripWithTenant[];
  tripPlacements: Record<string, string>;

  // Filters
  statusFilter: string;
  setStatusFilter: (filter: string) => void;
  tailNumberFilter: string;
  setTailNumberFilter: (filter: string) => void;
  showFilters?: boolean; // Whether to show filter UI

  // Display properties
  loading?: boolean;
  startDate?: Date;
  endDate?: Date;

  // Callbacks
  onTripEdit?: (trip: TripWithTenant) => void;

  // Helper functions
  formatDate: (dateString: string) => string;
  getTripAssignments: (trip: TripWithTenant) => string;

  // CSR Status change handler
  onCsrStatusChange?: (tripId: string, newStatus: CsrStatus) => void;

  // Bulk operation props
  selectedIds: Set<string>;
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  selectedTrips: TripWithTenant[];
  hangars: Array<{ id: string; name: string }>;
  ramps: Array<{ id: string; name: string }>;
  onBulkStatusChange: (status: CsrStatus) => Promise<void>;
  onBulkDelete: () => void;
  onBulkCancel: () => void;
  onBulkAssignLocation: (
    locationId: string,
    locationType: 'hangar' | 'ramp',
    locationName: string
  ) => Promise<void>;
  onClearSelection: () => void;
  isProcessing?: boolean;
  canDelete?: boolean;

  // Confirmation dialogs
  showDeleteConfirmation: boolean;
  setShowDeleteConfirmation: (show: boolean) => void;
  onConfirmDelete: () => void;
  showCancelConfirmation: boolean;
  setShowCancelConfirmation: (show: boolean) => void;
  onConfirmCancel: () => void;
}

export const ScheduleListPresenter: React.FC<ScheduleListPresenterProps> = ({
  filteredTrips,
  trips,
  statusFilter,
  setStatusFilter,
  tailNumberFilter,
  setTailNumberFilter,
  showFilters = true,
  loading = false,
  startDate,
  endDate,
  onTripEdit,
  formatDate,
  getTripAssignments,
  onCsrStatusChange,
  // Bulk operation props
  selectedIds,
  setSelectedIds,
  selectedTrips,
  hangars,
  ramps,
  onBulkStatusChange,
  onBulkDelete,
  onBulkCancel,
  onBulkAssignLocation,
  onClearSelection,
  isProcessing = false,
  canDelete = true,
  // Confirmation dialogs
  showDeleteConfirmation,
  setShowDeleteConfirmation,
  onConfirmDelete,
  showCancelConfirmation,
  setShowCancelConfirmation,
  onConfirmCancel,
}) => {
  const { t } = useTranslation();

  // Keyboard shortcuts for bulk operations
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in an input
      const target = e.target as HTMLElement;
      if (['INPUT', 'TEXTAREA'].includes(target.tagName)) return;

      // Delete key - trigger bulk delete confirmation
      if (e.key === 'Delete' && selectedIds.size > 0) {
        e.preventDefault();
        onBulkDelete();
      }

      // Escape - clear selection
      if (e.key === 'Escape' && selectedIds.size > 0) {
        e.preventDefault();
        onClearSelection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIds, onBulkDelete, onClearSelection]);

  // Handle DataGrid selection change
  const handleSelectionChange = (newModel: GridRowSelectionModel) => {
    setSelectedIds(new Set(newModel as string[]));
  };

  // DataGrid column definitions
  const columns: GridColDef[] = [
    {
      field: "tail_number",
      headerName: t('screens.schedule.scheduleList.columns.tailNumber'),
      width: 200,
      align: "left",
      filterable: true,
      sortable: true,
      renderCell: (params) => {
        // Get trip type from tenant data
        const tenantType = params.row.aircraft_type; // This is actually the tenant type field
        const tripType: TripType = getTripType(tenantType);
        const typeColor = tripTypeColors[tripType];
        const typeLabel = tripTypeShortLabels[tripType];

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%", gap: 1 }}>
            <Typography variant="body2" fontWeight="medium">
              {params.value || "—"}
            </Typography>
            <Chip
              label={typeLabel}
              size="small"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                borderColor: `${typeColor}60`,
                color: typeColor,
                bgcolor: `${typeColor}10`,
                '& .MuiChip-label': {
                  px: 0.75,
                },
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "aircraft_model",
      headerName: t('screens.schedule.scheduleList.columns.aircraftModel'),
      width: 200,
      align: "left",
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2">{params.value || "—"}</Typography>
        </Box>
      ),
    },
    {
      field: "arrival_date",
      headerName: t('screens.schedule.scheduleList.columns.arrival'),
      width: 180,
      type: "dateTime",
      filterable: true,
      sortable: true,
      valueGetter: (_, row) => parseISO(row.arrival_date),
      renderCell: (params) => formatDate(params.row.arrival_date),
    },
    {
      field: "departure_date",
      headerName: t('screens.schedule.scheduleList.columns.departure'),
      width: 180,
      type: "dateTime",
      filterable: true,
      sortable: true,
      valueGetter: (_, row) => {
        // Handle infinity dates for sorting (sort to end)
        if (isInfinityDate(row.departure_date)) {
          return new Date("2099-12-31T23:59:59Z");
        }
        return parseISO(row.departure_date);
      },
      renderCell: (params) => formatDate(params.row.departure_date),
    },
    {
      field: "csr_status",
      headerName: t('screens.schedule.scheduleList.columns.status'),
      width: 150,
      filterable: true,
      sortable: true,
      valueGetter: (_, row) => row.csr_status || "requested",
      renderCell: (params) => {
        const currentStatus = (params.row.csr_status || "requested") as CsrStatus;
        return (
          <CsrStatusSelect
            value={currentStatus}
            onChange={(newStatus) => onCsrStatusChange?.(params.row.trip_id, newStatus)}
            variant="standard"
          />
        );
      },
    },
    {
      field: "notes",
      headerName: t('screens.schedule.scheduleList.columns.notes'),
      width: 200,
      align: "left",
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.value ? (
            <Tooltip title={params.value} arrow>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  maxWidth: 180,
                  cursor: "help",
                }}
              >
                {params.value}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "owner_manager",
      headerName: t('screens.schedule.scheduleList.columns.ownerManager'),
      width: 180,
      align: "left",
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2">{params.value || "—"}</Typography>
        </Box>
      ),
    },
    {
      field: "assignment",
      headerName: t('screens.schedule.scheduleList.columns.assignment'),
      width: 180,
      align: "left",
      filterable: true,
      sortable: true,
      valueGetter: (_, row) => getTripAssignments(row),
    },
    {
      field: "actions",
      headerName: t('screens.schedule.scheduleList.columns.actions'),
      width: 100,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => onTripEdit?.(params.row)}
          title={t('screens.schedule.scheduleList.tooltips.editTrip')}
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {/* Date range indicator */}
      {startDate && endDate && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('screens.schedule.scheduleList.dateRange', {
            startDate: format(startDate, "MMM d, yyyy"),
            endDate: format(endDate, "MMM d, yyyy")
          })}
        </Typography>
      )}

      {/* Filters - only show if not controlled from parent */}
      {showFilters && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            label={t('screens.schedule.scheduleList.filters.tailNumberLabel')}
            variant="outlined"
            size="small"
            value={tailNumberFilter}
            onChange={(e) => setTailNumberFilter(e.target.value)}
            sx={{ minWidth: 200 }}
            placeholder={t('screens.schedule.scheduleList.filters.tailNumberPlaceholder')}
          />

          <CsrStatusFilterSelect
            value={statusFilter}
            onChange={setStatusFilter}
            translationNamespace="scheduleList"
          />

          <Box sx={{ flexGrow: 1 }} />

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ alignSelf: "center" }}
          >
            {t('screens.schedule.scheduleList.tripCount', { filtered: filteredTrips.length, total: trips.length })}
          </Typography>
        </Stack>
      )}

      {/* Trip count when filters are hidden */}
      {!showFilters && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, textAlign: "right" }}
        >
          {t('screens.schedule.scheduleList.tripCount', { filtered: filteredTrips.length, total: trips.length })}
        </Typography>
      )}

      {/* DataGrid Table */}
      <Paper sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={filteredTrips}
          columns={columns}
          getRowId={(row) => row.trip_id}
          // Enable sorting & filtering
          sortingOrder={["asc", "desc"]}
          filterMode="client"
          // Pagination
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 50 } },
          }}
          // Checkbox selection for bulk operations
          checkboxSelection
          rowSelectionModel={Array.from(selectedIds)}
          onRowSelectionModelChange={handleSelectionChange}
          // UI Configuration - keep this so checkbox is the only way to select
          disableRowSelectionOnClick
          // Loading state
          loading={loading}
          // No rows overlay
          slots={{
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                <Typography>{t('screens.schedule.scheduleList.noTripsFound')}</Typography>
              </Stack>
            ),
          }}
        />
      </Paper>

      {/* Bulk Action Bar */}
      <ScheduleBulkActionBar
        selectedTrips={selectedTrips}
        hangars={hangars}
        ramps={ramps}
        onBulkStatusChange={onBulkStatusChange}
        onBulkDelete={onBulkDelete}
        onBulkCancel={onBulkCancel}
        onBulkAssignLocation={onBulkAssignLocation}
        onClearSelection={onClearSelection}
        isProcessing={isProcessing}
        canDelete={canDelete}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirmation && (
        <ConfirmationDialog
          title={t('screens.schedule.scheduleList.bulkActions.deleteConfirmTitle')}
          text={t('screens.schedule.scheduleList.bulkActions.deleteConfirmMessage', {
            count: selectedIds.size,
          })}
          okText={t('screens.schedule.scheduleList.bulkActions.deleteConfirmButton')}
          okButtonColor="error"
          onCancel={() => setShowDeleteConfirmation(false)}
          onOk={onConfirmDelete}
          onClose={() => setShowDeleteConfirmation(false)}
        />
      )}

      {/* Cancel Confirmation Dialog */}
      {showCancelConfirmation && (
        <ConfirmationDialog
          title={t('screens.schedule.scheduleList.bulkActions.cancelConfirmTitle')}
          text={t('screens.schedule.scheduleList.bulkActions.cancelConfirmMessage', {
            count: selectedIds.size,
          })}
          okText={t('screens.schedule.scheduleList.bulkActions.cancelConfirmButton')}
          okButtonColor="warning"
          onCancel={() => setShowCancelConfirmation(false)}
          onOk={onConfirmCancel}
          onClose={() => setShowCancelConfirmation(false)}
        />
      )}
    </Box>
  );
};
